import BaseApi from '@/api/Base/BaseApi'
import {
  CreateInferenceNodePayload,
  UpdateInferenceNodePayload,
  InferenceNodeInfo,
  GetInferenceStatusResponse,
  GetInferenceResultResponse,
  StartInferencePayload,
  UpdateThresholdPayload,
  ExportInferenceDatasetPayload,
  ExportDatasetPayload,
  ExportDatasetResponse,
  GetVideoResultPayload,
  GetVideoResultResponse,
  VideoHeatmapItem,
  GetVideoHeatmapPayload
} from '@/api/InferenceNode/types'
import {
  CommonValidationResult,
  GetValidationInformationResponse,
  GetValidationResultItemsResponse,
  ValidationResultItemsPayload,
  VideoSummaryResult
} from '@/api/ValidationNode/types'
import { CommonListPayload, DatasetNodeItemListResponse } from '@/api/DatasetNode/types'
// import { GetLabelingNodeItemsResponse } from '@/api/LabelingNode/types'

const ROOT = '/inference'

const defaultPaginationOptions = {
  itemsPerPage: 40,
  page: 0,
  sortDir: 'desc',
  sortKey: 'createdAt',
}

export default class InferenceNodeApi extends BaseApi {
  createInferenceNode(payload: CreateInferenceNodePayload) {
    const { projectId, ...rest } = payload
    return this.agent.post<InferenceNodeInfo>(`/project/${projectId}/inference/create`, rest)
  }

  getInferenceNodeDetail(inferenceNodeId: string) {
    return this.agent.get<InferenceNodeInfo>(`${ROOT}/${inferenceNodeId}`)
  }

  updateInferenceNode(payload: UpdateInferenceNodePayload) {
    const { inferenceNodeId, name, ...rest } = payload
    return this.agent.put<InferenceNodeInfo>(`${ROOT}/${inferenceNodeId}`, { ...rest, name: name?.trim() })
  }

  deleteInferenceNode(inferenceNodeId: string) {
    return this.agent.delete(`${ROOT}/${inferenceNodeId}`)
  }

  startInference(payload: StartInferencePayload) {
    const { inferenceNodeId, ...rest } = payload
    return this.agent.post<GetInferenceStatusResponse>(`${ROOT}/${inferenceNodeId}/inference`, rest)
  }

  stopInference(inferenceNodeId: string) {
    return this.agent.put<GetInferenceStatusResponse>(`${ROOT}/${inferenceNodeId}/inference/stop`)
  }

  updateThreshold(payload: UpdateThresholdPayload) {
    const { inferenceNodeId, ...rest } = payload
    return this.agent.put<GetInferenceStatusResponse>(`${ROOT}/${inferenceNodeId}/threshold`, rest)
  }

  getInferenceStatus(inferenceNodeId: string) {
    return this.agent.get<GetInferenceStatusResponse>(`${ROOT}/${inferenceNodeId}/inference/status`)
  }

  getInferenceResult(inferenceNodeId: string) {
    return this.agent.get<GetInferenceResultResponse>(`${ROOT}/${inferenceNodeId}/inference`)
  }

  getInferenceInformation(inferenceNodeId: string) {
    return this.agent.get<GetValidationInformationResponse>(`${ROOT}/${inferenceNodeId}/information`)
  }

  getInferenceItems(inferenceNodeId: string, options?: CommonListPayload) {
    const { itemsPerPage, page, sortDir, sortKey } = options ?? defaultPaginationOptions
    return this.agent.get<DatasetNodeItemListResponse>(`${ROOT}/${inferenceNodeId}/items`, {
      params: {
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getInferenceResultSummary(inferenceNodeId: string) {
    return this.agent.get<CommonValidationResult>(`${ROOT}/${inferenceNodeId}/summary`)
  }

  getInferenceResultItems(inferenceNodeId: string, options?: ValidationResultItemsPayload) {
    const {
      isNG,
      includeDetectedObjects,
      filterClassIds,
      hasNoPredictedClass,
      keyword,
      page,
      itemsPerPage,
      sortDir,
      sortKey,
    } = options ?? { ...defaultPaginationOptions }

    return this.agent.get<GetValidationResultItemsResponse>(`${ROOT}/${inferenceNodeId}/result-items`, {
      params: {
        isNG,
        includeDetectedObjects,
        filterClassIds,
        hasNoPredictedClass,
        // 파일명 부분 일치 검색
        keyword,
        page,
        num_per_page: itemsPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  downloadInferenceReport(inferenceNodeId: string) {
    return this.agent.get<Blob>(`${ROOT}/${inferenceNodeId}/report`, {
      responseType: 'blob',
    })
  }

  exportInferenceDataset(payload: ExportInferenceDatasetPayload) {
    const { inferenceNodeId, nodeName, ...rest } = payload
    return this.agent.post(`${ROOT}/${inferenceNodeId}/exports`, rest, {
      responseType: 'blob',
    })
  }

  exportDataset(payload: ExportDatasetPayload) {
    const { inferenceNodeId, ...rest } = payload
    return this.agent.post<ExportDatasetResponse>(`${ROOT}/${inferenceNodeId}/export-dataset`, rest)
  }

  getTabularResult(inferenceNodeId: string) {
    return this.agent.get<any>(`${ROOT}/${inferenceNodeId}/tabular-result`)
  }

  getVideoSummary(inferenceNodeId: string) {
    return this.agent.get<VideoSummaryResult>(`${ROOT}/${inferenceNodeId}/video-summary`)
  }

  getVideoResult(payload: GetVideoResultPayload) {
    const { inferenceNodeId, page, numPerPage, sortKey, sortDir } = payload
    return this.agent.get<GetVideoResultResponse>(`${ROOT}/${inferenceNodeId}/video-result`, {
      params: {
        page,
        num_per_page: numPerPage,
        sort_key: sortKey,
        sort_dir: sortDir,
      },
    })
  }

  getVideoHeatmap(payload: GetVideoHeatmapPayload) {
    const { inferenceNodeId, startMs, endMs } = payload
    return this.agent.get<VideoHeatmapItem[]>(`${ROOT}/${inferenceNodeId}/video-heatmap`, {
      params: { start_ms: startMs, end_ms: endMs },
    })
  }
}
